import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const TherapyForTeens = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const helpItems = [
    "Anxiety, depression, or persistent low mood",
    "School pressure, perfectionism, or academic burnout",
    "Social stress, bullying, or friendship conflicts",
    "Identity exploration — including gender, sexuality, and self-expression",
    "Family conflict or communication breakdowns",
    "Low self-esteem, self-doubt, or negative self-talk",
    "Difficulty managing emotions or impulse control",
    "Life transitions — new school, divorce, grief, or loss"
  ];

  const fitItems = [
    "Your teen is pulling away, shutting down, or acting out more than usual",
    "They seem anxious, stressed, or overwhelmed but won't open up to you",
    "School feels like a constant source of pressure or avoidance",
    "They need a trusted adult outside the family to talk to",
    "You've noticed shifts in mood, sleep, appetite, or motivation",
    "You want a therapist who actually connects with teens — not one who talks at them"
  ];

  return (
    <div className="therapy-site" data-testid="therapy-for-teens-page">
      <Helmet>
        <title>Therapy for Teens | Inspire Therapy Collective - Lake Nona, Orlando</title>
        <meta name="description" content="A grounded, relatable space for teens working through anxiety, identity, school pressure, and mental health struggles in Lake Nona, Orlando and throughout Florida and Virginia." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div 
          className="service-hero-overlay" 
          style={{ backgroundImage: "url('https://images.pexels.com/photos/35720174/pexels-photo-35720174.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
        />
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="teens-page-title">Therapy for Teens</h1>
          <p className="service-tagline">
            A grounded, relatable space for teens working through identity, relationships, school pressure, and mental health struggles.
          </p>
          <span className="service-location-tag">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              The teen years are intense. Between school, social dynamics, identity questions, and the pressure to have it all figured out, it's no surprise that so many teens are struggling — even the ones who look like they're holding it together.
            </p>
            <p>
              Sessions with me are conversational and real — no worksheets, no lectures, no awkward silences. I meet teens where they are, building trust through genuine connection and creating a space where they can be fully honest without fear of judgment.
            </p>
            <p>
              I help teens develop emotional tools that actually stick — not just coping strategies for the therapy room, but real skills they can use in their everyday lives. My goal is to help your teen feel more grounded, more confident, and more connected to who they really are.
            </p>
          </div>
        </div>
      </section>

      {/* What I Help With */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="teens-help-heading">What I Help With</h2>
          <div className="service-help-grid">
            {helpItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`helpIconTeen${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#helpIconTeen${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#helpIconTeen${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">My Approach with Teens</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerTeens" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerTeens)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerTeens)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              I use a blend of CBT, ACT (Acceptance and Commitment Therapy), and Motivational Interviewing — adapted for the way teens actually think and communicate. No clinical jargon, no forced exercises. Just real conversation and practical strategies.
            </p>
            <p>
              I also work closely with parents when appropriate. Teens need autonomy in therapy, but families often need support too. I help bridge the communication gap between parents and teens so everyone feels heard and respected.
            </p>
            <p>
              Whether your teen needs help navigating anxiety, building self-confidence, or just processing the chaos of adolescence — I'm here to walk alongside them.
            </p>
          </div>
        </div>
      </section>

      {/* Good Fit */}
      <section className="service-fit-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">This Might Be a Good Fit If...</h2>
          <div className="service-fit-list">
            {fitItems.map((item, index) => (
              <div key={index} className="service-fit-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="fit-bullet" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step Up Scholarship Callout */}
      <section className="service-intro" data-testid="step-up-callout-teens">
        <div className="container">
          <div className="step-up-callout fade-in-up">
            <p>Enrolled in a Step Up For Students scholarship? We're on the EMA Marketplace and bill your child's scholarship directly for counseling.</p>
            <a href="/step-up-scholarship" className="step-up-callout-link" data-testid="step-up-link-teens">Learn more &rarr;</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Ready to Support Your Teen?</h2>
            <p>
              Your teen doesn't have to be in crisis to benefit from therapy. If something feels off, trust that instinct. Let's talk about what they need and how I can help.
            </p>
            <div className="hero-cta-group" style={{ justifyContent: 'center' }}>
              <button onClick={goToContact} className="btn btn-primary" data-testid="teens-cta-primary">
                Book a Free 15-Min Consult
              </button>
              <button onClick={() => navigate('/', { state: { scrollTo: 'insurance' } })} className="btn btn-secondary-outline" data-testid="teens-cta-secondary">
                Check Insurance & Book
              </button>
            </div>
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="explore-all-services-link">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TherapyForTeens;
